/**
 * StockDataPlanViewDialog - Visualização (somente leitura) de um plano de dados
 *
 * Aberto pela coluna "Plano de dados" da lista de chips. Dados via RPC
 * `get_stock_data_plan_detail` (permite quem vê Chips ou Planos de dados).
 *
 * RELEASE: 2026-10-08 v1.0.0 - Versão inicial.
 */
import { Loader2, Signal } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { dataPlanCarrierLabel, formatPackageMb, useStockDataPlanDetail } from '@/hooks/useStockRegistries';

const brl = (n: number | null | undefined, digits = 2) =>
  n === null || n === undefined ? '—' : n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: digits, maximumFractionDigits: digits });
const plain = (v: number | null | undefined, suffix = '') => (v === null || v === undefined ? '—' : `${v}${suffix}`);

interface Props {
  planId: string | null;
  onOpenChange: (open: boolean) => void;
}

/** Linha rótulo/valor do detalhe. */
function Info({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="space-y-0.5">
      <p className="text-xs text-muted-foreground">{label}</p>
      <div className="text-sm font-medium">{value}</div>
    </div>
  );
}

export function StockDataPlanViewDialog({ planId, onOpenChange }: Props) {
  const { data: p, isLoading } = useStockDataPlanDetail(planId);

  return (
    <Dialog open={!!planId} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90dvh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="rounded-xl bg-primary/10 p-2.5"><Signal className="h-5 w-5 text-primary" /></div>
            <div className="text-left">
              <DialogTitle>{p?.name ?? 'Plano de dados'}</DialogTitle>
              <DialogDescription>Detalhes do plano vinculado ao chip.</DialogDescription>
            </div>
          </div>
        </DialogHeader>
        {isLoading || !p ? (
          <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
        ) : (
          <div className="space-y-5">
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
              <Info label="Fornecedor" value={p.supplier_name ?? '—'} />
              <Info label="Pacote" value={formatPackageMb(p.data_package_mb)} />
              <Info label="Situação" value={p.is_active ? 'Ativo' : 'Inativo'} />
              <Info label="Fidelidade" value={plain(p.loyalty_months, ' meses')} />
              <Info label="Carência" value={plain(p.grace_days, ' dias')} />
            </div>
            <Info label="Operadoras compatíveis" value={
              <div className="flex flex-wrap gap-1 pt-1">{p.compatible_carriers.map((c) => <Badge key={c} variant="secondary">{dataPlanCarrierLabel(c)}</Badge>)}</div>
            } />
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
              <Info label="Valor original" value={brl(p.original_amount)} />
              <Info label="Valor negociado" value={brl(p.negotiated_amount)} />
              <Info label="Multa por chip" value={brl(p.penalty_per_chip)} />
              <Info label="Dia vencimento" value={plain(p.due_day)} />
              <Info label="Dia fechamento" value={plain(p.closing_day)} />
              <Info label="Valor SMS" value={brl(p.sms_amount)} />
              <Info label="Excedente (MB)" value={brl(p.overage_mb_amount, 3)} />
              <Info label="Excedente (GB)" value={brl(p.overage_gb_amount)} />
            </div>
            {p.notes && <Info label="Observações" value={<p className="whitespace-pre-wrap font-normal">{p.notes}</p>} />}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
